/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { debounce } from 'lodash'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import type {
  Criteria,
  EuiComboBoxOptionOption,
  EuiTableFieldDataColumnType,
  Pagination,
  Search,
} from '@elastic/eui'
import { EuiComboBox, EuiInMemoryTable, EuiText } from '@elastic/eui'

import { CCM_ROLES } from './constants'
import { formatCCMClusterA11yNameWithId, formatCCMClusterNameWithId } from './lib'
import messages, { roleMessages } from './messages'

import type { FC } from 'react'
import type { CCMRole, CCMRoleAssignment, CCMSearchableTableProps, ConnectedCluster } from './types'

type CCMComboBoxRoleOption = EuiComboBoxOptionOption<CCMRole>

const DEBOUNCE_DELAY_MS = 300

const debounced = debounce((cb) => cb(), DEBOUNCE_DELAY_MS)

const PAGINATION_THRESHOLD = 10

const CCMAssignmentsSearchableTable: FC<CCMSearchableTableProps> = ({
  roleAssignments,
  isDisabled,
  onChangeCCMRoleAssignments,
}) => {
  const { formatMessage } = useIntl()

  const [pageIndex, setPageIndex] = useState(0)

  const [filterQuery, setFilterQuery] = useState('')

  const filteredItems = roleAssignments.filter((item) => {
    const { name, clusterUuid } = item.connectedCluster

    const lowercaseFilterQuery = filterQuery.toLowerCase()

    return (
      name.toLowerCase().includes(lowercaseFilterQuery) ||
      clusterUuid.toLowerCase().includes(lowercaseFilterQuery)
    )
  })

  const pagination: Pagination | undefined =
    filteredItems.length > PAGINATION_THRESHOLD
      ? {
          pageIndex,
          totalItemCount: filteredItems.length,
          showPerPageOptions: false,
        }
      : undefined

  const ccmRoleOptions = CCM_ROLES.map((role) => ({
    value: role,
    text: formatMessage(roleMessages[role]),
  }))

  const ccmRoleColumns: Array<EuiTableFieldDataColumnType<CCMRoleAssignment>> = [
    {
      name: formatMessage(messages.clusterTableColumnLabel),
      field: 'connectedCluster',
      render: (connectedCluster: ConnectedCluster) => (
        <EuiText size='s' aria-label={formatCCMClusterA11yNameWithId(connectedCluster)}>
          {formatCCMClusterNameWithId(connectedCluster)}
        </EuiText>
      ),
      truncateText: true,
      width: '50%',
    },
    {
      name: formatMessage(messages.rolesTableColumnLabel),
      field: 'roles',
      render: (roles: CCMRole[], ccmRoleAssignment: CCMRoleAssignment) => (
        <EuiComboBox<CCMRole>
          singleSelection={true}
          options={ccmRoleOptions.map<CCMComboBoxRoleOption>((option) => ({
            label: option.text,
            value: option.value,
          }))}
          selectedOptions={roles.map<CCMComboBoxRoleOption>((role: CCMRole) => ({
            label: formatMessage(roleMessages[role]),
            value: role,
          }))}
          onChange={(options: CCMComboBoxRoleOption[]) => {
            const selectedRoles = options.map((option) => option.value as CCMRole)

            onChangeCCMRoleAssignments({
              ...ccmRoleAssignment,
              roles: selectedRoles,
            })
          }}
          isClearable={true}
          data-test-subj='ccm-role-combo-box'
          aria-label={`Select roles for Connected cluster ${formatCCMClusterA11yNameWithId(
            ccmRoleAssignment.connectedCluster,
          )}`}
          isDisabled={isDisabled}
        />
      ),
      width: '50%',
    },
  ]
  const search: Search = {
    onChange: ({ query }) => debounced(() => setFilterQuery(query?.text ?? '')),
    box: {
      incremental: true,
    },
  }

  return (
    <EuiInMemoryTable<CCMRoleAssignment>
      items={filteredItems}
      columns={ccmRoleColumns}
      pagination={pagination}
      search={search}
      data-test-subj='ccmRoleAssignmentsTable'
      onChange={(criteria: Criteria<CCMRoleAssignment>) => setPageIndex(criteria.page?.index ?? 0)}
    />
  )
}

export default CCMAssignmentsSearchableTable
